import {
  VIDEO_PRODUCTION_DEFAULT_ADAPT_TYPE,
  VIDEO_PRODUCTION_SLOT_KIND,
  VIDEO_TEMPLATE_STATUS,
  VIDEO_TEMPLATE_TYPE,
  VIDEO_TEXT_FIT_RUNTIME_LIMITS,
} from '../constants.js';

const MEDIA_SLOT_KINDS = new Set([
  VIDEO_PRODUCTION_SLOT_KIND.IMAGE,
  VIDEO_PRODUCTION_SLOT_KIND.VIDEO,
]);
const VALID_SLOT_KINDS = new Set(Object.values(VIDEO_PRODUCTION_SLOT_KIND));
export const TEXT_FIT_RUNTIME_KEYS = [
  'reference_size',
  'max_chars_per_line',
  'max_lines',
];
const TEXT_FIT_RUNTIME_LABELS = {
  reference_size: '参考字号',
  max_chars_per_line: '参考字号下每行字数',
  max_lines: '单个插槽最多行数',
};
export const SLOT_GROUP_INPUT_MODE = {
  MULTI_SELECT: 'multi_select',
  SPLIT_TEXT: 'split_text',
};
export const TEMPLATE_SLOT_EDITOR_ENTRY_TYPE = {
  GROUP: 'group',
  SLOT: 'slot',
};

const SLOT_KIND_LABELS = {
  [VIDEO_PRODUCTION_SLOT_KIND.IMAGE]: '图片',
  [VIDEO_PRODUCTION_SLOT_KIND.VIDEO]: '视频',
  [VIDEO_PRODUCTION_SLOT_KIND.TEXT]: '文字',
};

const normalizeAdaptType = (value) => {
  if (value === undefined || value === null || value === '') {
    return VIDEO_PRODUCTION_DEFAULT_ADAPT_TYPE;
  }
  const parsed = Number(value);
  return [0, 1, 2, 3].includes(parsed)
    ? parsed
    : VIDEO_PRODUCTION_DEFAULT_ADAPT_TYPE;
};

const normalizeConfig = (config, kind) => {
  const next =
    config && typeof config === 'object' && !Array.isArray(config)
      ? { ...config }
      : {};

  if (MEDIA_SLOT_KINDS.has(kind)) {
    next.adapt_type = normalizeAdaptType(next.adapt_type);
  }

  if (kind === VIDEO_PRODUCTION_SLOT_KIND.TEXT) {
    delete next.adapt_type;
    delete next.adapt;
    delete next.size;
  }

  return next;
};

const normalizePositiveInteger = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : '';
};

const normalizeRuntime = (runtime = {}) => {
  const next =
    runtime && typeof runtime === 'object' && !Array.isArray(runtime)
      ? { ...runtime }
      : {};
  [...TEXT_FIT_RUNTIME_KEYS, 'max_length'].forEach((key) => {
    if (key in next) {
      const normalizedValue = normalizePositiveInteger(next[key]);
      if (normalizedValue) {
        next[key] = normalizedValue;
      } else {
        delete next[key];
      }
    }
  });
  return next;
};

export const getTextFitRuntimeValidationError = (runtime = {}, label = '文字配置') => {
  const normalized =
    runtime && typeof runtime === 'object' && !Array.isArray(runtime)
      ? runtime
      : {};
  const configuredKeys = TEXT_FIT_RUNTIME_KEYS.filter(
    (key) => normalized[key] !== undefined && normalized[key] !== null && normalized[key] !== ''
  );

  if (configuredKeys.length === 0) return '';
  if (configuredKeys.length !== TEXT_FIT_RUNTIME_KEYS.length) {
    return `${label}的参考字号、参考字号下每行字数和单个插槽最多行数必须同时填写`;
  }
  const values = Object.fromEntries(
    TEXT_FIT_RUNTIME_KEYS.map((key) => [key, normalizePositiveInteger(normalized[key])])
  );
  if (Object.values(values).some((value) => !value)) {
    return `${label}的字号适配配置必须是正整数`;
  }
  const outOfRangeKey = TEXT_FIT_RUNTIME_KEYS.find(
    (key) => values[key] > VIDEO_TEXT_FIT_RUNTIME_LIMITS[key].max
  );
  if (outOfRangeKey) {
    return `${label}的${TEXT_FIT_RUNTIME_LABELS[outOfRangeKey]}不能超过 ${VIDEO_TEXT_FIT_RUNTIME_LIMITS[outOfRangeKey].max}`;
  }
  return '';
};

const normalizeDefaultContent = (slot, kind) =>
  kind === VIDEO_PRODUCTION_SLOT_KIND.TEXT
    ? String(slot?.default_content || '').trim()
    : '';

const normalizeGroupKey = (value) => String(value || '').trim();
const getSlotEntryId = (slot) => `slot:${slot.key}`;
const getGroupEntryId = (groupKey) => `group:${groupKey}`;
const getIdValue = (id) => String(id || '');
const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object || {}, key);

const moveArrayItem = (items, fromIndex, toIndex) => {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length ||
    fromIndex === toIndex
  ) {
    return items;
  }

  const nextItems = [...items];
  const [item] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, item);
  return nextItems;
};

const buildNextGroupKey = (kind, groupConfig = {}) => {
  const base = `${kind || 'slot'}_group`;
  let index = 1;
  let key = `${base}_${index}`;
  while (groupConfig[key]) {
    index += 1;
    key = `${base}_${index}`;
  }
  return key;
};

const getDefaultGroupConfig = (kind) => ({
  name: `${getTemplateSlotKindLabel(kind)}组`,
  description: '',
  kind,
  config: MEDIA_SLOT_KINDS.has(kind) ? { adapt_type: VIDEO_PRODUCTION_DEFAULT_ADAPT_TYPE } : {},
  runtime: {
    input_mode: MEDIA_SLOT_KINDS.has(kind)
      ? SLOT_GROUP_INPUT_MODE.MULTI_SELECT
      : SLOT_GROUP_INPUT_MODE.SPLIT_TEXT,
  },
});

const getDefaultGroupName = (kind) => `${getTemplateSlotKindLabel(kind)}组`;

const normalizeGroupName = (config, kind, groupKey) => {
  if (!hasOwn(config, 'name')) return getDefaultGroupName(kind);
  const name = String(config?.name || '').trim();
  return name === groupKey ? getDefaultGroupName(kind) : name;
};

export const normalizeTemplateSlots = (slots = []) =>
  (Array.isArray(slots) ? slots : []).map((slot) => {
    const kind = slot.kind || VIDEO_PRODUCTION_SLOT_KIND.IMAGE;
    return {
      key: slot.key || '',
      description:
        typeof slot.description === 'string' ? slot.description : '',
      default_content: normalizeDefaultContent(slot, kind),
      kind,
      required: true,
      group_key: normalizeGroupKey(slot.group_key),
      runtime: normalizeRuntime(slot.runtime),
      config: normalizeConfig(slot.config, kind),
    };
  });

export const serializeTemplateSlots = (slots = []) =>
  (Array.isArray(slots) ? slots : []).map((slot) => {
    const key = String(slot.key || '').trim();
    const kind = slot.kind || VIDEO_PRODUCTION_SLOT_KIND.IMAGE;
    const defaultContent = normalizeDefaultContent(slot, kind);
    return {
      key,
      description: String(slot.description || '').trim(),
      ...(defaultContent ? { default_content: defaultContent } : {}),
      kind,
      required: true,
      ...(normalizeGroupKey(slot.group_key)
        ? { group_key: normalizeGroupKey(slot.group_key) }
        : {}),
      ...(Object.keys(normalizeRuntime(slot.runtime)).length
        ? { runtime: normalizeRuntime(slot.runtime) }
        : {}),
      config: normalizeConfig(slot.config, kind),
    };
  });

export const normalizeSlotGroupConfig = (groupConfig = {}) =>
  Object.fromEntries(
    Object.entries(
      groupConfig && typeof groupConfig === 'object' && !Array.isArray(groupConfig)
        ? groupConfig
        : {}
    ).map(([groupKey, config]) => {
      const kind = VALID_SLOT_KINDS.has(config?.kind)
        ? config.kind
        : VIDEO_PRODUCTION_SLOT_KIND.IMAGE;
      return [
        normalizeGroupKey(groupKey),
        {
          name: normalizeGroupName(config, kind, normalizeGroupKey(groupKey)),
          description: String(config?.description || '').trim(),
          kind,
          config: normalizeConfig(config?.config, kind),
          runtime: normalizeRuntime(config?.runtime),
        },
      ];
    }).filter(([groupKey]) => groupKey)
  );

export const serializeSlotGroupConfig = (groupConfig = {}) =>
  Object.fromEntries(
    Object.entries(normalizeSlotGroupConfig(groupConfig)).map(
      ([groupKey, config]) => [
        groupKey,
        {
          name: String(config.name || '').trim(),
          description: String(config.description || '').trim(),
          kind: config.kind,
          config: normalizeConfig(config.config, config.kind),
          runtime: normalizeRuntime(config.runtime),
        },
      ]
    )
  );

export const buildTemplateSlotEditorEntries = ({
  slots = [],
  groupConfig = {},
  expandedGroupKeys = [],
} = {}) => {
  const normalizedSlots = normalizeTemplateSlots(slots);
  const normalizedGroups = normalizeSlotGroupConfig(groupConfig);
  const emittedGroupKeys = new Set();
  const expandedGroupKeySet = new Set(
    (Array.isArray(expandedGroupKeys) ? expandedGroupKeys : [])
      .map(normalizeGroupKey)
      .filter(Boolean)
  );
  const slotCountByGroupKey = normalizedSlots.reduce((counts, slot) => {
    const groupKey = normalizeGroupKey(slot.group_key);
    const group = normalizedGroups[groupKey];
    if (group && group.kind === slot.kind) {
      counts[groupKey] = (counts[groupKey] || 0) + 1;
    }
    return counts;
  }, {});
  const slotIndexesByGroupKey = normalizedSlots.reduce((indexes, slot, index) => {
    const groupKey = normalizeGroupKey(slot.group_key);
    const group = normalizedGroups[groupKey];
    if (group && group.kind === slot.kind) {
      indexes[groupKey] = [...(indexes[groupKey] || []), index];
    }
    return indexes;
  }, {});

  const entries = Object.entries(normalizedGroups)
    .filter(([groupKey]) => !slotCountByGroupKey[groupKey])
    .map(([groupKey, group]) => ({
      type: TEMPLATE_SLOT_EDITOR_ENTRY_TYPE.GROUP,
      id: getGroupEntryId(groupKey),
      groupKey,
      group,
      slotCount: 0,
      slotIndexes: [],
      expanded: expandedGroupKeySet.has(groupKey),
    }));

  normalizedSlots.forEach((slot, index) => {
    const groupKey = normalizeGroupKey(slot.group_key);
    const group = normalizedGroups[groupKey];
    const hasMatchingGroup = group && group.kind === slot.kind;

    if (hasMatchingGroup && !emittedGroupKeys.has(groupKey)) {
      entries.push({
        type: TEMPLATE_SLOT_EDITOR_ENTRY_TYPE.GROUP,
        id: getGroupEntryId(groupKey),
        groupKey,
        group,
        slotCount: slotCountByGroupKey[groupKey],
        slotIndexes: slotIndexesByGroupKey[groupKey] || [],
        slots: (slotIndexesByGroupKey[groupKey] || []).map(
          (slotIndex) => normalizedSlots[slotIndex]
        ),
        expanded: expandedGroupKeySet.has(groupKey),
      });
      emittedGroupKeys.add(groupKey);
      if (expandedGroupKeySet.has(groupKey)) {
        (slotIndexesByGroupKey[groupKey] || []).forEach((slotIndex) => {
          entries.push({
            type: TEMPLATE_SLOT_EDITOR_ENTRY_TYPE.SLOT,
            id: getSlotEntryId(normalizedSlots[slotIndex]),
            slot: normalizedSlots[slotIndex],
            index: slotIndex,
            groupKey,
            insideGroup: true,
          });
        });
      }
      return;
    }

    if (hasMatchingGroup && emittedGroupKeys.has(groupKey)) {
      return;
    }

    entries.push({
      type: TEMPLATE_SLOT_EDITOR_ENTRY_TYPE.SLOT,
      id: getSlotEntryId(slot),
      slot,
      index,
      insideGroup: false,
    });
  });

  return entries;
};

export const getTemplateSlotEditorEntryId = (entry) => entry?.id || '';

export const createTemplateSlotGroupFromSelection = ({
  slots = [],
  groupConfig = {},
  selectedSlotKeys = [],
} = {}) => {
  const normalizedSlots = normalizeTemplateSlots(slots);
  const normalizedGroups = normalizeSlotGroupConfig(groupConfig);
  const selectedKeySet = new Set(
    (Array.isArray(selectedSlotKeys) ? selectedSlotKeys : [])
      .map((key) => String(key || '').trim())
      .filter(Boolean)
  );

  if (selectedKeySet.size < 2) {
    throw new Error('至少选择两个插槽');
  }

  const selectedSlots = normalizedSlots.filter((slot) =>
    selectedKeySet.has(slot.key)
  );
  if (selectedSlots.length !== selectedKeySet.size) {
    throw new Error('选择的插槽不存在');
  }
  if (selectedSlots.some((slot) => normalizeGroupKey(slot.group_key))) {
    throw new Error('已分组插槽不能再次合并');
  }

  const selectedKinds = new Set(selectedSlots.map((slot) => slot.kind));
  if (selectedKinds.size !== 1) {
    throw new Error('只能合并同类型插槽');
  }

  const [kind] = selectedKinds;
  const groupKey = buildNextGroupKey(kind, normalizedGroups);
  const groupedSlots = selectedSlots.map((slot) => ({
    ...slot,
    group_key: groupKey,
  }));
  const firstSelectedIndex = normalizedSlots.findIndex((slot) =>
    selectedKeySet.has(slot.key)
  );
  const unselectedSlots = normalizedSlots.filter(
    (slot) => !selectedKeySet.has(slot.key)
  );
  const insertIndex = normalizedSlots
    .slice(0, firstSelectedIndex)
    .filter((slot) => !selectedKeySet.has(slot.key)).length;

  return {
    groupKey,
    groupConfig: {
      ...normalizedGroups,
      [groupKey]: getDefaultGroupConfig(kind),
    },
    slots: [
      ...unselectedSlots.slice(0, insertIndex),
      ...groupedSlots,
      ...unselectedSlots.slice(insertIndex),
    ],
  };
};

const getEntryById = (entries, id) =>
  entries.find((entry) => getTemplateSlotEditorEntryId(entry) === id);

const getSlotGroupKey = (slot) => normalizeGroupKey(slot.group_key);

const getGroupedSlotKeys = (slots, groupKey) =>
  slots
    .filter((slot) => getSlotGroupKey(slot) === groupKey)
    .map((slot) => slot.key);

const replaceGroupSlotOrder = ({ slots, groupKey, nextGroupSlotKeys }) => {
  const slotByKey = new Map(slots.map((slot) => [slot.key, slot]));
  const groupKeySet = new Set(nextGroupSlotKeys);
  const firstGroupIndex = slots.findIndex(
    (slot) => getSlotGroupKey(slot) === groupKey
  );
  const remainingSlots = slots.filter((slot) => !groupKeySet.has(slot.key));
  const insertIndex = slots
    .slice(0, firstGroupIndex)
    .filter((slot) => !groupKeySet.has(slot.key)).length;
  const orderedGroupSlots = nextGroupSlotKeys
    .map((slotKey) => slotByKey.get(slotKey))
    .filter(Boolean);

  return [
    ...remainingSlots.slice(0, insertIndex),
    ...orderedGroupSlots,
    ...remainingSlots.slice(insertIndex),
  ];
};

const getTopLevelUnits = ({ slots, groupConfig }) => {
  const normalizedGroups = normalizeSlotGroupConfig(groupConfig);
  const emittedGroupKeys = new Set();
  const units = [];

  slots.forEach((slot) => {
    const groupKey = getSlotGroupKey(slot);
    const group = normalizedGroups[groupKey];
    if (group && group.kind === slot.kind) {
      if (!emittedGroupKeys.has(groupKey)) {
        units.push({
          id: getGroupEntryId(groupKey),
          groupKey,
          slotKeys: getGroupedSlotKeys(slots, groupKey),
        });
        emittedGroupKeys.add(groupKey);
      }
      return;
    }

    units.push({
      id: getSlotEntryId(slot),
      slotKeys: [slot.key],
    });
  });

  return units;
};

const getTopLevelIdForEntry = (entry) => {
  if (!entry) return '';
  if (entry.type === TEMPLATE_SLOT_EDITOR_ENTRY_TYPE.GROUP) {
    return getTemplateSlotEditorEntryId(entry);
  }
  return entry.insideGroup && entry.groupKey
    ? getGroupEntryId(entry.groupKey)
    : getTemplateSlotEditorEntryId(entry);
};

export const moveTemplateSlotEditorEntry = ({
  slots = [],
  groupConfig = {},
  activeId,
  overId,
  expandedGroupKeys = [],
} = {}) => {
  const normalizedSlots = normalizeTemplateSlots(slots);
  const entries = buildTemplateSlotEditorEntries({
    slots: normalizedSlots,
    groupConfig,
    expandedGroupKeys,
  });
  const activeEntry = getEntryById(entries, getIdValue(activeId));
  const overEntry = getEntryById(entries, getIdValue(overId));
  if (!activeEntry || !overEntry) return normalizedSlots;

  if (
    activeEntry.type === TEMPLATE_SLOT_EDITOR_ENTRY_TYPE.SLOT &&
    overEntry.type === TEMPLATE_SLOT_EDITOR_ENTRY_TYPE.SLOT &&
    activeEntry.insideGroup &&
    overEntry.insideGroup &&
    activeEntry.groupKey === overEntry.groupKey
  ) {
    const groupSlotKeys = getGroupedSlotKeys(normalizedSlots, activeEntry.groupKey);
    const nextGroupSlotKeys = moveArrayItem(
      groupSlotKeys,
      groupSlotKeys.indexOf(activeEntry.slot.key),
      groupSlotKeys.indexOf(overEntry.slot.key)
    );
    return replaceGroupSlotOrder({
      slots: normalizedSlots,
      groupKey: activeEntry.groupKey,
      nextGroupSlotKeys,
    });
  }

  if (activeEntry.insideGroup) return normalizedSlots;

  const activeTopLevelId = getTopLevelIdForEntry(activeEntry);
  const overTopLevelId = getTopLevelIdForEntry(overEntry);
  const units = getTopLevelUnits({ slots: normalizedSlots, groupConfig });
  const nextUnits = moveArrayItem(
    units,
    units.findIndex((unit) => unit.id === activeTopLevelId),
    units.findIndex((unit) => unit.id === overTopLevelId)
  );
  const slotByKey = new Map(normalizedSlots.map((slot) => [slot.key, slot]));

  return nextUnits
    .flatMap((unit) => unit.slotKeys)
    .map((slotKey) => slotByKey.get(slotKey))
    .filter(Boolean);
};

export const splitTemplateSlotGroups = ({
  slots = [],
  groupConfig = {},
  groupKeys = [],
} = {}) => {
  const groupKeySet = new Set(
    (Array.isArray(groupKeys) ? groupKeys : [])
      .map(normalizeGroupKey)
      .filter(Boolean)
  );
  if (groupKeySet.size === 0) {
    return {
      slots: normalizeTemplateSlots(slots),
      groupConfig: normalizeSlotGroupConfig(groupConfig),
    };
  }

  const normalizedGroups = normalizeSlotGroupConfig(groupConfig);
  const nextGroupConfig = Object.fromEntries(
    Object.entries(normalizedGroups).filter(
      ([groupKey]) => !groupKeySet.has(groupKey)
    )
  );
  const nextSlots = normalizeTemplateSlots(slots).map((slot) => {
    if (!groupKeySet.has(getSlotGroupKey(slot))) return slot;
    const { group_key: _groupKey, ...nextSlot } = slot;
    return nextSlot;
  });

  return {
    slots: nextSlots,
    groupConfig: nextGroupConfig,
  };
};

export const getTemplateSlotKindLabel = (kind) =>
  SLOT_KIND_LABELS[kind] || kind || '-';

export const moveTemplateSlot = (slots = [], fromIndex, toIndex) => {
  if (!Array.isArray(slots)) return [];
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= slots.length ||
    toIndex >= slots.length ||
    fromIndex === toIndex
  ) {
    return slots;
  }

  const nextSlots = [...slots];
  const [slot] = nextSlots.splice(fromIndex, 1);
  nextSlots.splice(toIndex, 0, slot);
  return nextSlots;
};

export const shouldShowParseSlotsButton = (template) =>
  template?.status === 'Available' &&
  (Array.isArray(template.slot_config) ? template.slot_config.length === 0 : true);

export const shouldShowSyncOrdinaryTemplateButton = (template) =>
  template?.template_type === VIDEO_TEMPLATE_TYPE.TIMELINE;

export const shouldShowRefreshAdvancedTemplateButton = (template) =>
  template?.template_type === VIDEO_TEMPLATE_TYPE.VE_TEMPLATE &&
  ![
    VIDEO_TEMPLATE_STATUS.AVAILABLE,
    VIDEO_TEMPLATE_STATUS.UPLOAD_FAILED,
    VIDEO_TEMPLATE_STATUS.PROCESS_FAILED,
  ].includes(template?.status);

export const isVideoTemplatePageBusy = ({
  loading = false,
  creating = false,
  savingId = '',
  deletingId = '',
  parsingId = '',
  syncingId = '',
} = {}) =>
  !!loading || !!creating || !!savingId || !!deletingId || !!parsingId || !!syncingId;
